import ProductShopSelector from "../../shops/ProductShopSelector";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { GrClose } from "react-icons/gr";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { isAuthenticated } from "../../auth";
import { getCategoriesByName } from "../../auth/apiCategory";
import { laguangeElakay } from "../../pageInside/dataElakay";
import CatSelected from "../../pages/categories/catSelected";
import CatSearch from "../../pages/categories/catSearch";
import SalesSimulation from "../../pageInside/salesSimulation";
import ImagesLoading from "../../pageInside/imagesLoadingAdd";
import ProductPriceInput from "../../pageInside/ProductPriceInput";
import useCategoryFilters from "../../pageInside/productCreation/useCategoryFilters";
import { createProviderProduct, getOwnProviderProduct, setProviderProductImages, softDeleteProviderProduct, updateProviderProduct, uploadProviderProductImage } from "../services/providerProductService";
import { toImageUrl } from "../../../config";
import { MdAttachMoney, MdCalculate, MdCategory, MdEdit, MdFilterList, MdLocalShipping, MdVisibility } from "react-icons/md";
import ProviderProductPreview from "./ProviderProductPreview";
import ProviderCategoriesManager from "./ProviderCategoriesManager";
import ProviderFiltersEditor, { toDefs } from "./ProviderFiltersEditor";
import useLinkedCategories from "../hooks/useLinkedCategories";
import { dropshippingText } from "../i18n";
import ProviderCommercialSections from "./ProviderCommercialSections";
import { buildPayload, emptyFormState, formStateFromProduct } from "./ProviderProductForm";
import ProviderSalesSimulation from "./ProviderSalesSimulation";
import ProviderSellingIntro from "./ProviderSellingIntro";
import countries from "../../../countries.json";
// Stepper (.wrapper-progressBar/.progressBar), .boxCategory1, .form-group... :
// exactement les mêmes classes/CSS que Sell Your Product.
import "../../pages/addProduct.css";
import "./ProviderProductForm.css";
// Onglets/en-têtes de Seller Edit Product (.edit-product-container, .nav-tabs-modern, .nav-tab-btn)
import "../../pages/editProduct/EditProduct.css";
import "./ProviderProductWizard.css";
import { elakayAutoText } from "../../pageInside/dataElakay";


// Add Provider Product -- même workflow visuel que Sell Your Product
// (pages/addProduct.jsx) : 1 Categories -> 2 Info -> 3 Filters -> 4 Sales Simulation -> 5 Images.
// Réutilise tels quels CatSelected/CatSearch (catégories), le catalogue de
// filtres dynamiques (useCategoryFilters), ImagesLoading (upload) et
// Sales Simulation Provider ; seuls les champs métier diffèrent (Provider price, stock
// source, paliers gros, options de dropshipping, zones).
//
// Comme Seller : le produit est créé en brouillon à la fin de l'étape Info,
// complété à l'étape Filters, puis les images sont téléversées sur le produit
// existant. Le backend pose providerId lui-même (jamais lu du client).
const STEP = { CATEGORIES: 1, INFO: 2, FILTERS: 3, SIMULATION: 4, IMAGES: 5 };

const generic = (infoSource, namespace, key, fallback) => {
  try {
    return infoSource?.laguangeElakay?.(namespace, infoSource.languageAPI)?.[key] || fallback;
  } catch {
    return fallback;
  }
};

// `editProductId` => mode EDIT (/provider/products/:id/edit) : le MÊME wizard
// charge le produit existant via l'API, saute l'accueil, ne repasse jamais le
// produit en brouillon et met à jour le même document (jamais de re-création).
const ProviderProductWizard = ({ infoSource, editProductId = null }) => {
  const isEdit = Boolean(editProductId);
  const navigate = useNavigate();
  const t = dropshippingText(infoSource, "providerProductWizard");
  const g = (ns, key, fallback) => generic(infoSource, ns, key, fallback);

  const catalog = useCategoryFilters();
  const { category, items, loading, loadCategory } = catalog;
  // Multi-catégories + filtres/variantes : état UNIQUE partagé par Add et Edit.
  const [linkedIds, setLinkedIds] = useState([]);
  const [sourceFilters, setSourceFilters] = useState({}); // filterId -> valeurs autorisées
  const [variants, setVariants] = useState([]); // [{ filter: {clé: valeur}, quantity }]
  // Units already reserved by open customer orders (server counter, read-only).
  const [reservedUnits, setReservedUnits] = useState(0);
  // Variants the server keeps (a row without any filter value is ignored there too).
  const realVariants = variants.filter((v) => Object.keys(v.filter || {}).length > 0);
  const hasVariants = realVariants.length > 0;
  const variantTotal = realVariants.reduce((total, v) => total + Math.max(0, Math.floor(Number(v.quantity) || 0)), 0);
  const [displayFilters, setDisplayFilters] = useState([]);
  const [linking, setLinking] = useState(false);
  const linkedCategories = useLinkedCategories(linkedIds);

  const [step, setStep] = useState(STEP.CATEGORIES);
  const [maxStep, setMaxStep] = useState(STEP.CATEGORIES);
  const [browseBySelection, setBrowseBySelection] = useState(false);
  const [search, setSearch] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  // Écran d'accueil (accordéon + Get Started / Sales Simulation), comme Sell
  // Your Product ; sauté quand on reprend un brouillon (?draft=).
  const [started, setStarted] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [simSearch, setSimSearch] = useState([]);
  const [simSearchInput, setSimSearchInput] = useState("");
  // SalesSimulation (calculateur générique Elakay, sans produit) appelle
  // loadCategory() en interne : catalogue séparé pour ne pas changer la
  // catégorie choisie dans le wizard.
  const simulationCatalog = useCategoryFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  // Reprise après refresh : ?draft=<id>&step=<n> (jamais uniquement du state
  // React, perdu au refresh). Le produit est relu via l'API (ownership vérifié
  // côté serveur), le formulaire et la catégorie sont ré-hydratés.
  const draftParam = editProductId || searchParams.get("draft");
  const [restoring, setRestoring] = useState(Boolean(draftParam));
  const [simulationKey, setSimulationKey] = useState(0);
  const [simStatus, setSimStatus] = useState(null); // "ok" | "invalid" | "error" | null

  const [form, setForm] = useState(emptyFormState);
  const [productId, setProductId] = useState(null);
  const [images, setImages] = useState([]);
  const EDIT_TAB_IDS = ["info", "categories", "filters", "pricing", "fulfillment", "simulation", "preview"];
  const [tab, setTab] = useState(EDIT_TAB_IDS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "info");
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!draftParam) {
      loadCategory();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const token = isAuthenticated()?.token;
      const product = await getOwnProviderProduct(token, draftParam);
      if (cancelled) return;
      if (product?.success === false || product?.error || !product?._id) {
        setError(product?.error || elakayAutoText("h_d477b0ab92edfe", "Could not load the product."));
        if (isEdit) setLoadError(product?.error || "Could not load the product.");
        setStarted(true); // l'erreur s'affiche dans l'étape Catégories, pas sur l'accueil
        setRestoring(false);
        loadCategory();
        return;
      }
      setForm(formStateFromProduct(product));
      setImages(Array.isArray(product.images) ? product.images : []);
      setLinkedIds((product.categoryIds?.length ? product.categoryIds : product.categoryId ? [product.categoryId] : []).map(String));
      setSourceFilters(Object.fromEntries((product.sourceFilters || []).map((f) => [String(f.filterId), [...(f.values || [])]])));
      setVariants((product.variants || []).map((v) => ({ filter: { ...(v.filter || {}) }, quantity: v.quantity ?? 0 })));
      setReservedUnits(Number(product.customerReserved) || 0);
      setDisplayFilters(product.displayFilters || []);
      setProductId(product._id);
      if (product.categoryId) {
        await loadCategory(product.categoryId);
        // (les filtres viennent des catégories liées : voir useLinkedCategories)
      } else {
        await loadCategory();
      }
      if (isEdit) {
        setStarted(true);
        setRestoring(false);
        return;
      }
      const wanted = Number(searchParams.get("step"));
      const next = wanted >= STEP.INFO && wanted <= STEP.IMAGES ? wanted : STEP.INFO;
      setStep(next);
      setMaxStep(STEP.IMAGES);
      setStarted(true);
      setRestoring(false);
    })();
    return () => {
      cancelled = true;
    };
    // Hydratation unique au montage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Garde ?draft/&step dans l'URL dès qu'un produit existe.
  useEffect(() => {
    if (productId && !restoring) setSearchParams(isEdit ? { tab } : { draft: productId, step: String(step) }, { replace: true });
  }, [productId, step, tab, restoring, isEdit, setSearchParams]);

  const setField = (key) => (e) => {
    const value = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const goTo = (next) => {
    setError("");
    if (next === STEP.SIMULATION) {
      setSimStatus(null);
      setSimulationKey((k) => k + 1);
    }
    if (isEdit) {
      // EDIT : les étapes du wizard sont des onglets (même navigation que Seller Edit Product).
      setTab({ 1: "categories", 2: "info", 3: "filters", 4: "simulation", 5: "filters" }[next] || "info");
      return;
    }
    setStep(next);
    setMaxStep((prev) => Math.max(prev, next));
  };

  // Catégorie finale (feuille) = sans sous-catégories, comme Seller (addProduct.jsx).

  // Une catégorie FINALE choisie est LIÉE au produit (sans doublon) ; les autres
  // catégories déjà liées sont conservées. Add : on passe à Info ; Edit : on reste.
  const browse = async (categoryId) => {
    const data = await loadCategory(categoryId);
    if (data && (data.items || []).length === 0 && data.cartegory?._id) {
      const id = String(data.cartegory._id);
      setLinkedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setLinking(false);
      if (!isEdit) goTo(STEP.INFO);
    }
  };
  // Retirer une catégorie = retirer la RELATION ; le produit n'est jamais supprimé.
  const unlinkCategory = (id) => setLinkedIds((prev) => prev.filter((c) => c !== id));

  // Structure envoyée au backend seulement quand les catégories liées sont chargées
  // (sinon on risquerait d'écraser des filtres enregistrés par une liste vide).
  const structureReady = linkedIds.length === 0 || (!linkedCategories.loading && linkedCategories.linked.every((c) => c.name.length > 0));
  const filterDefs = toDefs(linkedCategories.filterList, infoSource, linkedCategories.filterIdOf);
  const structurePayload = () => {
    if (!structureReady) return {};
    const known = new Set(filterDefs.map((d) => d.filterId));
    return {
      sourceFilters: Object.entries(sourceFilters)
        .filter(([id, values]) => known.has(id) && values.length > 0)
        .map(([filterId, values]) => ({ filterId, values })),
      variants: variants
        .map((v) => ({ filter: Object.fromEntries(Object.entries(v.filter || {}).filter(([, val]) => val)), quantity: Math.max(0, Number(v.quantity) || 0) }))
        .filter((v) => Object.keys(v.filter).length > 0),
      displayFilters,
    };
  };

  const ButtonBackCategory = () => {
    const parentId = category?.categoryID?.length > 1 ? category.categoryID[1] : undefined;
    return (
      <span onClick={() => browse(parentId)} className="h5 float-left" role="button" tabIndex={0}>
        <FaLongArrowAltLeft />
      </span>
    );
  };

  const authToken = () => isAuthenticated()?.token;

  // spec Provider -- jamais d'`images` dans un PATCH : les images sont écrites
  // uniquement par l'endpoint d'upload, un PATCH avec `images: []` les effacerait.
  const savePayload = (extra = {}) => {
    const { images, ...rest } = buildPayload(form);
    return {
      ...rest,
      // Edit d'un produit sans catégorie : ne jamais lui attribuer la catégorie racine
      // affichée par défaut (seule une catégorie finale choisie est enregistrée).
      categoryId: linkedIds[0] || null,
      categoryIds: linkedIds,
      ...(linkedCategories.linked[0]?.name?.length ? { categoryPath: [...linkedCategories.linked[0].name].reverse() } : {}),
      ...structurePayload(),
      ...extra,
    };
  };

  const persist = async (payload) => {
    const payment = form.dropshippingPayment || {};
    const paymentFee = payment.sellerPaymentAcceptanceFee || {};
    if (payment.sellerPaymentAcceptanceAllowed && paymentFee.type === "fixed" && Number(paymentFee.value) > 0.75) {
      setError(t("paymentAcceptanceFixedLimit", elakayAutoText("h_512bfbe93c5161", "Maximum allowed fixed fee is $0.75.")));
      return null;
    }
    if (payment.sellerPaymentAcceptanceAllowed && paymentFee.type === "percentage" && (Number(paymentFee.value) < 0 || Number(paymentFee.value) > 100)) {
      setError(t("paymentAcceptancePercentageLimit", elakayAutoText("h_2ba8c477ca855e", "Percentage fee must be between 0 and 100.")));
      return null;
    }
    setBusy(true);
    const result = productId
      ? await updateProviderProduct(authToken(), productId, payload)
      : await createProviderProduct(authToken(), payload);
    setBusy(false);
    if (result?.success === false || result?.error) {
      setError(result.error || elakayAutoText("h_7cc1e0a8076019", "Could not save the product."));
      return null;
    }
    setProductId(result._id || productId);
    return result;
  };

  const submitInfo = async (e) => {
    e.preventDefault();
    if (linkedIds.length === 0) return setError(t("t013"));
    if (!form.name.trim()) return setError(t("t011"));
    if (!form.basePrice) return setError(t("t012"));
    if (await persist(savePayload(isEdit ? {} : { status: "draft", active: false }))) goTo(STEP.FILTERS);
  };

  // One quantity truth (server: providerProductService.syncStockWithVariants): with variants the
  // product's available quantity IS the sum of the variants' quantities; without variants it is the
  // single global quantity typed below.
  const stockValue = () => (hasVariants ? variantTotal : Number(form.stock) || 0);
  const stockMissing = () => !hasVariants && (form.stock === "" || Number(form.stock) < 0);

  const submitFilters = async (e) => {
    e.preventDefault();
    if (stockMissing()) return setError(t("t028"));
    if (await persist({ ...structurePayload(), stock: stockValue() })) goTo(STEP.SIMULATION);
  };

  // Simulation -> Images : la navigation ne dépend PAS d'un produit déjà en
  // base. Seul l'upload d'image a besoin d'un id : si aucun brouillon n'existe
  // encore, il est créé ici (draft, inactif, jamais publié) avant d'ouvrir Images.
  const nextToImages = async () => {
    if (!productId) {
      const saved = await persist(savePayload({ stock: stockValue(), status: "draft", active: false }));
      if (!saved) return;
    }
    goTo(STEP.IMAGES);
  };

  // EDIT : enregistre TOUT le formulaire sur le produit existant (même _id), puis
  // retourne à My Products. Publish n'est proposé que si le produit n'est pas actif.
  const saveChanges = async (publish) => {
    if (!form.name.trim()) return setError(t("t011"));
    if (!form.basePrice) return setError(t("t012"));
    if (stockMissing()) return setError(t("t028"));
    if (linkedIds.length === 0) return setError(t("t165"));
    const extra = { stock: stockValue(), ...(publish ? { status: "active", active: true } : {}) };
    if (await persist(savePayload(extra))) navigate("/provider/products");
  };

  const applyImages = async (next) => {
    setBusy(true);
    const result = await setProviderProductImages(authToken(), productId, next);
    setBusy(false);
    if (result?.success === false || result?.error) return setError(result.error || t("t133"));
    setError("");
    setImages(result.images || next);
  };

  const trashProduct = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(t("t127"))) return;
    setBusy(true);
    const result = await softDeleteProviderProduct(authToken(), productId);
    setBusy(false);
    if (result?.success === false || result?.error) return setError(result.error || t("t148"));
    // Deleted products go to the Provider Trash (restorable from there).
    navigate("/provider/trash");
  };

  const finish = async (publish) => {
    if (publish && !(await persist({ status: "active", active: true }))) return;
    navigate("/provider/products");
  };

  // Mirrors the server's completeness rule (providerProductCompleteness.js): defensive/UX only --
  // the backend re-checks the same fields on every write (assertProviderPublishable) and is the
  // real enforcement, so a stale/bypassed client check can never publish an incomplete product.
  const missingRequiredFields = useMemo(() => {
    const missing = [];
    if (!form.name.trim()) missing.push(g("sellPage", "name", "Name"));
    if (!form.description || !form.description.trim()) missing.push(g("sellPage", "description", "Description"));
    if (linkedIds.length === 0) missing.push(g("title", "Categories", "Categories"));
    if (!form.basePrice || Number(form.basePrice) <= 0) missing.push(t("t002"));
    if (images.length === 0) missing.push(g("title", "images", "Images"));
    return missing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.description, form.basePrice, linkedIds, images]);

  const missingFieldsHint = missingRequiredFields.length > 0 && (
    <p className="text-muted small provider-product-missing-hint">
      {t("t123")} {missingRequiredFields.join(", ")}
    </p>
  );

  // Données courantes du formulaire pour la Sales Simulation (mode CREATE) :
  // état unique du wizard, envoyé tel quel au backend pour validation + calcul.
  // Le produit n'a pas besoin d'exister en base ; providerId n'est jamais envoyé.
  const simulationData = useMemo(
    () => ({ ...savePayload(), stock: stockValue() }),
    // savePayload dépend de form + category
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, linkedIds, linkedCategories.linked, sourceFilters, variants, displayFilters]
  );

  const stepperClass = (n) => (step >= n ? "active" : "");
  const canOpen = (n) => n <= maxStep && (n === STEP.CATEGORIES || linkedIds.length > 0);

  const stepper = (
    <div className="row">
      <div className="col-xs-12 col-md-8 offset-md-2 block pb-4">
        <div className="wrapper-progressBar">
          <h2>{isEdit ? t("t124") : t("t001")}</h2>
          <ul className="progressBar">
            <li className={stepperClass(1)} onClick={() => canOpen(1) && goTo(1)}>
              {g("title", "Categories", "Categories")}
            </li>
            <li className={stepperClass(2)} onClick={() => canOpen(2) && goTo(2)}>
              {g("Button", "info", "Info")}
            </li>
            <li className={stepperClass(3)} onClick={() => canOpen(3) && productId && goTo(3)}>
              {g("title", "filters", "Filters")}
            </li>
            <li className={stepperClass(4)} onClick={() => canOpen(4) && goTo(4)}>
              {g("Button", "salesSimul", t("t024"))}
            </li>
            <li className={stepperClass(5)} onClick={() => canOpen(5) && productId && goTo(5)}>
              {g("title", "images", "Images")}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const categoryHeader = () => (
    <div className="boxCategory1 text-center" id="heading">
      <h2>{Array.isArray(category?.name) ? category.name[0] : ""}</h2>
      <span className="btn p-0 float-left" onClick={() => setStep(STEP.CATEGORIES)} role="button" tabIndex={0}>
        <FaLongArrowAltLeft />
      </span>
      <p>
        {Array.isArray(category?.name) &&
          category.name.map(
            (word, index) =>
              index !== 0 && (
                <span key={index}>
                  {word}
                  {index < category.name.length - 1 ? ", " : "."}
                </span>
              )
          )}
      </p>
    </div>
  );

  const errorBox = error ? <div className="alert alert-danger">{error}</div> : null;

  const nextButton = (enabled) => (
    <button className={`btn ${enabled ? "btn-primary" : "btn-secondary"} float-right`} disabled={busy}>
      {g("Button", "next", "Next")}
    </button>
  );

  const categoriesStep = (
    <>
      <div className="form-group">
        <h2>
          {browseBySelection && ButtonBackCategory()}
          {infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.addProduct?.t004 || elakayAutoText("h_7260cd5def999c", "elakay categories")}
        </h2>
        {[true, false].map((bySelection) => (
          <React.Fragment key={String(bySelection)}>
            <input
              className="form-check-input"
              type="radio"
              name="providerCatMode"
              id={`providerCatMode_${bySelection}`}
              checked={browseBySelection === bySelection}
              onChange={() => setBrowseBySelection(bySelection)}
            />
            <label className="form-check-label" htmlFor={`providerCatMode_${bySelection}`}>
              {bySelection ? elakayAutoText("h_431ac1e0ee03c3", "by selection") : elakayAutoText("h_14cb78c91dbd6e", "by name search")}&nbsp;&nbsp;&nbsp;
            </label>
          </React.Fragment>
        ))}
      </div>
      {errorBox}
      {loading && !category ? (
        <div className="alert alert-info">{g("title", "loanding", "Loading...")}</div>
      ) : browseBySelection ? (
        <CatSelected
          clickBack={browse}
          clickButton2={() => browse()}
          ButtonBackCategory={ButtonBackCategory}
          seleteCategory={() => (event) => browse(event.target.value || undefined)}
          laguangeElakay={laguangeElakay}
          infoSource={infoSource}
          categories={category || { name: [], items: [] }}
        />
      ) : (
        <CatSearch
          infoSource={infoSource}
          search={search}
          setSearch={setSearch}
          setSearchInput={setSearchInput}
          searchInput={searchInput}
          getCategoriesByName={getCategoriesByName}
          categories={category || {}}
          loadCategory={browse}
        />
      )}
    </>
  );

  // Blocs réutilisés à l'identique par le wizard (Add) et par les onglets d'Edit.
  const basicFields = (
    <>
      <ProductShopSelector type="provider" value={form.shopIds} onChange={(shopIds) => setForm((prev) => ({ ...prev, shopIds }))} />
      <div className="form-group">
        <label className="text-muted" htmlFor="pw-name">{g("sellPage", "name", "Name")}</label>
        <input id="pw-name" type="text" className="form-control" value={form.name} onChange={setField("name")} />
      </div>
      <div className="form-group">
        <label className="text-muted" htmlFor="pw-description">{g("sellPage", "description", "Description")}</label>
        <textarea id="pw-description" className="form-control" value={form.description} onChange={setField("description")} />
      </div>
      <div className="form-group">
        <label className="text-muted" htmlFor="pw-brand">{t("t019")}</label>
        <input id="pw-brand" type="text" className="form-control" value={form.brand} onChange={setField("brand")} />
      </div>
    </>
  );
  const priceField = (
    <div className="form-group">
      <label className="text-muted" htmlFor="pw-price">{t("t002")}</label>
      <div className="input-group">
        <div className="input-group-prepend">
          <span className="input-group-text">$</span>
        </div>
        <ProductPriceInput id="pw-price" className="form-control" placeholder="0.00" value={form.basePrice} onChange={setField("basePrice")} />
      </div>
      <small className="form-text text-muted">{t("t003")}</small>
    </div>
  );
  const skuFields = (
    <>
      <div className="form-group">
        <label className="text-muted" htmlFor="pw-sku">{t("t017")}</label>
        <input id="pw-sku" type="text" className="form-control" value={form.sku} onChange={setField("sku")} />
      </div>
      <div className="form-group">
        <label className="text-muted" htmlFor="pw-psku">{t("t018")}</label>
        <input id="pw-psku" type="text" className="form-control" value={form.providerSku} onChange={setField("providerSku")} />
      </div>
    </>
  );
  // Filtres = union des filtres des catégories liées (dédupliquée par id de filtre).
  const filterFields = (
    <>
      <section className="provider-made-filter-card" aria-labelledby="provider-made-filter-title">
        <div className="provider-made-filter-card__header">
          <div>
            <span className="provider-made-filter-card__eyebrow">{elakayAutoText("h_2fb5e4c673a51f", "Product origin")}</span>
            <h3 id="provider-made-filter-title">{g("title", "madeIn", "Made in / Country of origin")}</h3>
          </div>
          <span className="provider-made-filter-card__badge">ISO</span>
        </div>
        <p>{g("text", "madeInHint", "Select the country where the product is actually manufactured.")}</p>
        <label className="provider-made-filter-card__label" htmlFor="provider-made-country">
          {g("title", "selectCountry", "Select a country")}
        </label>
        <select id="provider-made-country" value={form.countryOfOrigin} onChange={setField("countryOfOrigin")}>
          <option value="">{g("title", "selectCountry", "Select a country")}</option>
          {countries.map((country) => <option key={country.code} value={country.code}>{country.name} ({country.code})</option>)}
        </select>
        {form.countryOfOrigin && <div className="provider-made-filter-card__selected">{elakayAutoText("h_2e0844789cb4b1", "Selected:")}<strong>{countries.find((country) => country.code === form.countryOfOrigin)?.name || form.countryOfOrigin}</strong> <span>({form.countryOfOrigin})</span></div>}
      </section>
      <ProviderFiltersEditor
        filterList={linkedCategories.filterList}
        filterIdOf={linkedCategories.filterIdOf}
        infoSource={infoSource}
        sourceFilters={sourceFilters}
        setSourceFilters={setSourceFilters}
        variants={variants}
        setVariants={setVariants}
        reservedUnits={isEdit ? reservedUnits : null}
        displayFilters={displayFilters}
        setDisplayFilters={setDisplayFilters}
        t={t}
      />
    </>
  );
  const categoriesManager = (
    <ProviderCategoriesManager linked={linkedCategories.linked} onRemove={unlinkCategory} onLink={() => (isEdit ? setLinking((v) => !v) : goTo(STEP.CATEGORIES))} linking={linking} t={t} />
  );
  const stockField = hasVariants ? (
    <div className="pw-stock-summary" data-testid="pw-stock-summary">
      <div><span>{elakayAutoText("h_pqty_total_available", "Total available quantity")}</span><strong>{variantTotal.toLocaleString()}</strong></div>
      {isEdit && <div><span>{elakayAutoText("h_pqty_reserved", "Reserved by open orders")}</span><strong>{reservedUnits.toLocaleString()}</strong></div>}
      {isEdit && <div><span>{elakayAutoText("h_pqty_physical", "Physical quantity")}</span><strong>{(variantTotal + reservedUnits).toLocaleString()}</strong></div>}
      <small>{elakayAutoText("h_pqty_from_variants", "Calculated from the quantity of each variant above. Edit a variant's quantity to change it.")}</small>
    </div>
  ) : (
    <div className="form-group">
      <label className="text-muted" htmlFor="pw-stock">{t("t004")}</label>
      <input id="pw-stock" type="number" min="0" className="form-control" value={form.stock} onChange={setField("stock")} />
      <small className="form-text text-muted">{t("t005")}</small>
      {isEdit && reservedUnits > 0 && <small className="form-text text-muted">{elakayAutoText("h_pqty_reserved", "Reserved by open orders")}: {reservedUnits.toLocaleString()} · {elakayAutoText("h_pqty_physical", "Physical quantity")}: {((Number(form.stock) || 0) + reservedUnits).toLocaleString()}</small>}
    </div>
  );

  const infoStep = (
    <>
      {categoryHeader()}
      <form className="mb-3" onSubmit={submitInfo}>
        {categoriesManager}
        {basicFields}
        {priceField}
        {skuFields}
        <div className="provider-product-form">
          <ProviderCommercialSections form={form} setField={setField} setForm={setForm} t={t} />
        </div>
        <small className="form-text text-muted">{t("t014")}</small>
        {errorBox}
        {nextButton(Boolean(form.name.trim() && form.basePrice))}
      </form>
    </>
  );

  const filtersStep = (
    <>
      {categoryHeader()}
      <form className="mb-3" onSubmit={submitFilters}>
        {filterFields}
        {stockField}
        {errorBox}
        {nextButton(hasVariants || form.stock !== "")}
      </form>
    </>
  );

  const simulationStep = (
    <>
      <ProviderSalesSimulation
        mode="create"
        infoSource={infoSource}
        token={authToken()}
        productData={simulationData}
        refreshKey={simulationKey}
        onStatusChange={setSimStatus}
      />
      <div className="d-flex justify-content-between mt-3">
        <button type="button" className="btn btn-secondary" onClick={() => goTo(STEP.INFO)}>
          {t("t075")}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || simStatus === "invalid"} onClick={nextToImages}>
          {g("Button", "next", "Next")}
        </button>
      </div>
    </>
  );

  const imageManager = isEdit && (
    <div className="provider-images mb-3">
      <h3 className="h6">{t("t128")}</h3>
      {images.length === 0 ? (
        <p className="text-muted">{t("t132")}</p>
      ) : (
        <ul className="provider-images__list">
          {images.map((url, index) => (
            <li key={url} className="provider-images__item">
              <img src={toImageUrl(url)} alt={`${form.name} ${index + 1}`} loading="lazy" />
              {index === 0 ? (
                <span className="badge badge-primary">{t("t131")}</span>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => applyImages([url, ...images.filter((u) => u !== url)])}>
                  {t("t129")}
                </button>
              )}
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => applyImages(images.filter((u) => u !== url))}>
                {t("t130")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const imageBlock = (
    <>
      {imageManager}
      <ImagesLoading
        laguangeElakay={laguangeElakay}
        infoSource={infoSource}
        imgSize={isEdit ? images.length : 0}
        maxImgSize={12}
        namProduct={form.name}
        uploadImage={async (file) => {
          const result = await uploadProviderProductImage(authToken(), productId, file);
          // Tracked in ADD mode too (not just Edit): the Publish button below needs the real
          // uploaded-image count to know whether the product can be completed, not a value that
          // stays permanently 0 until the next full reload.
          if (!result?.error && Array.isArray(result?.images)) setImages(result.images);
          return result;
        }}
        onUploaded={() => (isEdit ? undefined : finish(false))}
      />
    </>
  );

  const imagesStep = (
    <>
      {imageBlock}
      <div className="text-center mt-3">
        <button type="button" className="btn btn-outline-secondary mr-2" disabled={busy} onClick={() => goTo(STEP.SIMULATION)}>
          {t("t075")}
        </button>
        {isEdit ? (
          <>
            <button type="button" className="btn btn-primary mr-2" disabled={busy} onClick={() => saveChanges(false)}>
              {t("t125")}
            </button>
            {form.status !== "active" && (
              <button type="button" className="btn btn-success mr-2" disabled={busy || missingRequiredFields.length > 0} onClick={() => saveChanges(true)}>
                {t("t016")}
              </button>
            )}
            <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={trashProduct}>
              {t("t126")}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary mr-2" disabled={busy} onClick={() => finish(false)}>
              {t("t015")}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy || missingRequiredFields.length > 0} onClick={() => finish(true)}>
              {t("t016")}
            </button>
          </>
        )}
      </div>
      {form.status !== "active" && missingFieldsHint}
      {errorBox}
    </>
  );

  // ------------------------------------------------------------ EDIT layout
  // Même structure que Seller Edit Product : titre + onglets (.nav-tabs-modern) +
  // contenu de l'onglet, adaptée au métier Provider. Une seule barre d'actions :
  // Save met à jour le produit EXISTANT (même _id) avec TOUT le formulaire.
  const categoryName = linkedCategories.linked.map((c) => (Array.isArray(c.name) ? c.name.join(" › ") : "")).filter(Boolean).join(" · ");
  const filterRows = filterDefs
    .filter((d) => (sourceFilters[d.filterId] || []).length > 0)
    .map((d) => ({
      key: d.filterId,
      label: d.name,
      value: (sourceFilters[d.filterId] || []).map((val) => d.values.find((v) => v.value === val)?.label || val).join(", "),
    }));

  const openTab = (id) => {
    setError("");
    if (id === "simulation") {
      setSimStatus(null);
      setSimulationKey((k) => k + 1); // valeurs non enregistrées relues à chaque ouverture
    }
    setTab(id);
  };

  const editTabs = [
    { id: "info", label: g("Button", "info", "Info"), icon: <MdEdit /> },
    { id: "categories", label: g("title", "Categories", "Categories"), icon: <MdCategory /> },
    { id: "filters", label: g("title", "filtersImgs", "Filters & Images"), icon: <MdFilterList /> },
    { id: "pricing", label: t("t150"), icon: <MdAttachMoney /> },
    { id: "fulfillment", label: t("t151"), icon: <MdLocalShipping /> },
    { id: "simulation", label: g("Button", "salesSimul", t("t024")), icon: <MdCalculate /> },
    { id: "preview", label: t("t152"), icon: <MdVisibility /> },
  ];

  const editContent = {
    info: (
      <form className="mb-3" onSubmit={(e) => e.preventDefault()}>
        {basicFields}
        {skuFields}
      </form>
    ),
    categories: (
      <>
        {categoriesManager}
        {linking && categoriesStep}
      </>
    ),
    filters: (
      <>
        <form className="mb-3" onSubmit={(e) => e.preventDefault()}>{filterFields}{stockField}</form>
        {imageBlock}
      </>
    ),
    pricing: (
      <form className="mb-3 provider-product-form" onSubmit={(e) => e.preventDefault()}>
        {priceField}
        <ProviderCommercialSections form={form} setField={setField} setForm={setForm} t={t} show={["pricing"]} />
      </form>
    ),
    fulfillment: (
      <div className="provider-product-form">
        <ProviderCommercialSections form={form} setField={setField} setForm={setForm} t={t} show={["fulfillment", "zones"]} />
      </div>
    ),
    simulation: (
      <ProviderSalesSimulation
        mode="create"
        infoSource={infoSource}
        token={authToken()}
        productData={simulationData}
        refreshKey={simulationKey}
        onStatusChange={setSimStatus}
      />
    ),
    preview: <ProviderProductPreview infoSource={infoSource} form={form} images={images} categoryName={categoryName} filterRows={filterRows} />,
  };

  if (isEdit && !loadError && !restoring) {
    return (
      <div className="edit-product-container provider-edit">
        <div className="edit-product-header">
          <h1 className="page-title">{t("t124")}</h1>
          <p className="page-subtitle">{t("t153")}</p>
        </div>
        <div className="provider-edit-navigation">
          <div className="provider-edit-tabs" role="tablist" aria-label={t("t124")}>
            {editTabs.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`provider-edit-tab-${item.id}`}
                aria-controls="provider-edit-panel"
                aria-selected={tab === item.id}
                className={`provider-edit-tab${tab === item.id ? " provider-edit-tab--active" : ""}`}
                onClick={() => openTab(item.id)}
              >
                <span className="provider-edit-tab__icon" aria-hidden="true">{item.icon}</span>
                <span className="provider-edit-tab__label">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="edit-product-content" id="provider-edit-panel" role="tabpanel" aria-labelledby={`provider-edit-tab-${tab}`}>{editContent[tab]}</div>
        {errorBox}
        {form.status !== "active" && missingFieldsHint}
        <div className="provider-edit__actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => saveChanges(false)}>
            {busy ? t("t160") : t("t125")}
          </button>
          {form.status !== "active" && (
            <button type="button" className="btn btn-success" disabled={busy || missingRequiredFields.length > 0} onClick={() => saveChanges(true)}>
              {t("t016")}
            </button>
          )}
          <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => navigate("/provider/products")}>
            {g("Button", "cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-outline-danger ml-auto" disabled={busy} onClick={trashProduct}>
            {t("t126")}
          </button>
        </div>
      </div>
    );
  }

  if (isEdit && loadError) return <div className="alert alert-danger m-3" role="alert">{loadError}</div>;
  if (restoring) return <div className="alert alert-info m-3">{g("title", "loanding", "Loading...")}</div>;

  return (
    <>
      {stepper}
      <div className="row">
        <div className="col-md-8 offset-md-2 blockEl">
          <span className="btn p-2" onClick={() => navigate("/provider/products")} role="button" aria-label={elakayAutoText("h_bbfa773e5a63a5", "Close")}>
            <GrClose />
          </span>
          {step === STEP.CATEGORIES && !started && (
            <ProviderSellingIntro
              infoSource={infoSource}
              onGetStarted={() => setStarted(true)}
              onSalesSimulation={() => setShowCalculator(true)}
            />
          )}
          {step === STEP.CATEGORIES && started && categoriesStep}
          {step === STEP.INFO && infoStep}
          {step === STEP.FILTERS && filtersStep}
          {step === STEP.SIMULATION && simulationStep}
          {step === STEP.IMAGES && imagesStep}
        </div>
      </div>
      <Modal show={showCalculator} onHide={() => setShowCalculator(false)} size="xl" dialogClassName="modal-90w" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h2 className="text-center">{g("Button", "salesSimul", t("t024"))}</h2>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SalesSimulation
            infoSource={infoSource}
            laguangeElakay={laguangeElakay}
            search={simSearch}
            setSearch={setSimSearch}
            setSearchInput={setSimSearchInput}
            searchInput={simSearchInput}
            getCategoriesByName={getCategoriesByName}
            categories={simulationCatalog.category || {}}
            loadCategory={simulationCatalog.loadCategory}
            token={authToken()}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProviderProductWizard;
